import { isRef, onUnmounted, unref, watch } from 'vue';

/**
 * Simple audio helper that mimics the old PlaySound component API.
 */
export function useSoundPlayer({
    sound,
    devices = null,
    loop = false
} = {}) {
    let audio = null;
    let currentSoundSrc = null;
    let soundTimeout = null;
    let loopAttached = false;

    const ensureAudio = () => {
        const source = unref(sound);
        if (!source) {
            return null;
        }

        if (audio && currentSoundSrc === source) {
            return audio;
        }

        teardownLoop();
        if (audio) {
            audio.pause();
        }

        audio = new Audio(source);
        currentSoundSrc = source;
        loopAttached = false;

        return audio;
    };

    const playSound = () => {
        if (!audio) {
            return;
        }

        audio.play().catch((e) => {
            console.error("Audio Playback Error:", e.name, e.message);
        });
    };

    const scheduleReplay = () => {
        soundTimeout = setTimeout(() => {
            playSound();
        }, 2000);
    };

    const clearReplay = () => {
        if (soundTimeout) {
            clearTimeout(soundTimeout);
            soundTimeout = null;
        }
    };

    const attachLoop = () => {
        if (!loop || !audio || loopAttached) {
            return;
        }

        audio.addEventListener('ended', scheduleReplay);
        loopAttached = true;
    };

    const teardownLoop = () => {
        if (audio && loopAttached) {
            audio.removeEventListener('ended', scheduleReplay);
            loopAttached = false;
        }
    };

    const getActiveDeviceId = () => {
        const list = unref(devices) || [];
        if (!Array.isArray(list)) {
            return null;
        }

        const activeDevice = list.find(device => device?.active);
        return activeDevice?.deviceId ?? null;
    };

    const setAudioSpeaker = (deviceId = null) => {
        ensureAudio();
        if (!audio || typeof audio.setSinkId !== 'function') {
            return;
        }

        const targetId = deviceId || getActiveDeviceId();
        if (!targetId) {
            return;
        }

        audio.setSinkId(targetId).catch(() => {
            /* Ignore unsupported output device errors */
        });
    };

    const start = () => {
        ensureAudio();
        if (!audio) {
            return;
        }

        clearReplay();
        attachLoop();
        setAudioSpeaker();
        playSound();
    };

    const stop = () => {
        clearReplay();
        if (!audio) {
            return;
        }

        audio.pause();
        audio.currentTime = 0;
        teardownLoop();
    };

    // Unlock the audio object
    const init = () => {
        start();
        stop();
    };

    if (devices) {
        watch(() => unref(devices), () => {
            setAudioSpeaker();
        }, { deep: true });
    }

    if (isRef(sound)) {
        watch(() => unref(sound), () => {
            const wasPlaying = audio ? !audio.paused : false;
            ensureAudio();
            if (wasPlaying) {
                start();
            }
        });
    }

    onUnmounted(() => {
        stop();
        audio = null;
    });

    return {
        init,
        start,
        stop,
        end: stop,
        setAudioSpeaker
    };
}
